import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

export const GOOGLE_FORM_ID =
  "1FAIpQLScP37k7Krb8JSVvNiIQ15vord4Jg5dLK6G6hjihiD2rqEfc6g";

export const FORM_ENTRY_IDS = {
  name: "entry.1487737964",
  dob: "entry.1402729919",
  age: "entry.524712104",
  gender: "entry.918340039",
  weight: "entry.91922330",
  height: "entry.863364206",
  weightCategory: "entry.1178739042",
  mobile: "entry.810046461",
  email: "entry.1066058909",
  state: "entry.291849807",
  district: "entry.1784533720",
  pincode: "entry.340453629",
  address: "entry.215403456",
  bloodGroup: "entry.1514354913",
  guardianMobile: "entry.1789609336",
  uniformSize: "entry.1230798917",
};

const SENIOR_WEIGHT_CATS = {
  Male: [
    "45 kg", "48 kg", "51 kg", "54 kg", "57 kg", "60 kg", "63.5 kg",
    "67 kg", "71 kg", "76 kg", "81 kg", "86 kg", "91 kg", "96+ kg",
  ],
  Female: [
    "45 kg", "48 kg", "51 kg", "54 kg", "57 kg", "60 kg", "63.5 kg",
    "67 kg", "71 kg", "75 kg", "76 kg", "81 kg", "86 kg", "91 kg", "96+ kg",
  ],
};

const INDIAN_STATES = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam",
  "Bihar", "Chandigarh", "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka", "Kerala",
  "Ladakh", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim",
  "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
  "West Bengal",
];

const INITIAL_FN_FORM = {
  name: "",
  dob: "",
  gender: "",
  weight: "",
  height: "",
  weightCategory: "",
  mobile: "",
  email: "",
  state: "",
  district: "",
  pincode: "",
  address: "",
  bloodGroup: "",
  guardianMobile: "",
  uniformSize: "",
};

// Required field groups per section, used to derive completion + progress.
const SECTION_FIELDS = {
  "01": ["name", "dob", "gender"],
  "02": ["weight", "height", "weightCategory"],
  "03": ["mobile", "email", "state", "district", "pincode", "address"],
  "04": ["bloodGroup", "guardianMobile", "uniformSize"],
};

const ALL_REQUIRED = Object.values(SECTION_FIELDS).flat();

// ── Eligible age window. Lower bound covers the youngest age groups
//    (under-10 / 12 / 14 / 16 …); upper bound covers senior fighters.
const MIN_AGE = 6;
const MAX_AGE = 40;
const AGE_RANGE_LABEL = `${MIN_AGE}–${MAX_AGE}`;

function calcAge(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let a = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) a -= 1;
  return a;
}

// ── DOB picker constraints: only valid Senior 18–40 years are pickable.
function isoDateNYearsAgo(years) {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  // YYYY-MM-DD
  return d.toISOString().slice(0, 10);
}

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3.5 font-barlow text-[15px] text-white placeholder-white/35 transition-all duration-200 focus:border-wbc-gold focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-wbc-gold/30 [color-scheme:dark]";

function FieldLabel({ children, htmlFor, required = false, hint }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <label
        htmlFor={htmlFor}
        className="font-condensed block text-xs font-semibold uppercase tracking-[0.16em] text-white/80"
      >
        {children}
        {required && <span className="ml-1 text-wbc-red-bright">*</span>}
      </label>
      {hint && (
        <span className="font-condensed text-[10px] uppercase tracking-[0.18em] text-white/35">
          {hint}
        </span>
      )}
    </div>
  );
}

function CheckIcon({ className = "" }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
    >
      <path
        d="M3 7.5L6 10.5L11 4.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SectionHeader({ number, title, subtitle, complete }) {
  return (
    <div className="mb-5">
      <div className="flex items-baseline gap-3">
        <span
          className={`font-bebas text-2xl leading-none transition-colors ${
            complete ? "text-wbc-gold" : "text-wbc-gold-light/85"
          }`}
        >
          {number}
        </span>
        <span className="h-px flex-1 bg-gradient-to-r from-white/15 via-white/5 to-transparent" />
        <span className="font-condensed text-xs font-bold uppercase tracking-[0.28em] text-white">
          {title}
        </span>
        <AnimatePresence>
          {complete && (
            <motion.span
              key="check"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ type: "spring", stiffness: 460, damping: 22 }}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-wbc-gold/20 text-wbc-gold-light"
              aria-label="Section complete"
            >
              <CheckIcon />
            </motion.span>
          )}
        </AnimatePresence>
      </div>
      {subtitle && (
        <p className="mt-2 pl-9 font-barlow text-sm text-white/45">{subtitle}</p>
      )}
    </div>
  );
}

const sectionCls =
  "rounded-xl border border-white/[0.06] bg-white/[0.015] p-4 sm:p-6";

export default function RegisterModal({ open, onClose }) {
  const [formData, setFormData] = useState(INITIAL_FN_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const formRef = useRef(null);
  const firstFieldRef = useRef(null);
  const submitRef = useRef(null);

  const age = calcAge(formData.dob);
  const ageValid = age !== null && age >= MIN_AGE && age <= MAX_AGE;
  const weightCats =
    formData.gender && SENIOR_WEIGHT_CATS[formData.gender]
      ? SENIOR_WEIGHT_CATS[formData.gender]
      : [];

  // DOB picker: cap to the eligible age window (oldest → youngest)
  const dobMin = useMemo(() => isoDateNYearsAgo(MAX_AGE), []);
  const dobMax = useMemo(() => isoDateNYearsAgo(MIN_AGE), []);

  // Per-section completion (drives header tick + progress bar)
  const sectionCompletion = useMemo(() => {
    const out = {};
    Object.keys(SECTION_FIELDS).forEach((k) => {
      out[k] = SECTION_FIELDS[k].every(
        (f) => String(formData[f] ?? "").trim().length > 0
      );
    });
    return out;
  }, [formData]);

  const filledCount = ALL_REQUIRED.reduce(
    (n, f) => n + (String(formData[f] ?? "").trim().length > 0 ? 1 : 0),
    0
  );
  const progressPct = Math.round((filledCount / ALL_REQUIRED.length) * 100);
  const isDirty = filledCount > 0;

  const tryClose = () => {
    if (submitting) return;
    if (isDirty && !submitted) {
      const ok = window.confirm(
        "Close registration? Your filled details will be lost."
      );
      if (!ok) return;
    }
    onClose();
  };

  // body scroll lock + esc to close
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") tryClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDirty, submitting, submitted]);

  // reset transient state when fully closed
  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setErrorMsg("");
    }
  }, [open]);

  // focus first field when opened (skip on touch — avoids iOS keyboard pop)
  useEffect(() => {
    if (!open || submitted) return;
    const isTouch =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    if (isTouch) return;
    const t = setTimeout(() => firstFieldRef.current?.focus(), 250);
    return () => clearTimeout(t);
  }, [open, submitted]);

  // ── Single source of truth for state updates.
  //    `name` on each input stays as the Google Forms entry ID for submission;
  //    React state is keyed by semantic field name via this helper.
  const setField = (key, value) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      // resetting weight category whenever gender changes
      if (key === "gender") next.weightCategory = "";
      return next;
    });
  };

  const handleSubmit = (e) => {
    if (!formData.gender) {
      e.preventDefault();
      setErrorMsg("Please select your gender to continue.");
      return;
    }
    if (!ageValid) {
      e.preventDefault();
      setErrorMsg(`Registration accepts ages ${MIN_AGE} – ${MAX_AGE} only.`);
      return;
    }
    setErrorMsg("");
    setSubmitting(true);
    // Form submits to hidden iframe — onIframeLoad below flips submitted=true
  };

  const handleIframeLoad = () => {
    if (submitting) {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  const handleRegisterAnother = () => {
    setFormData(INITIAL_FN_FORM);
    setSubmitted(false);
    setErrorMsg("");
  };

  const formAction = `https://docs.google.com/forms/d/e/${GOOGLE_FORM_ID}/formResponse`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            onClick={tryClose}
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Senior Fighter Registration · WBC Open Championship 2026"
            className="relative z-[1] flex max-h-[96vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl border border-white/10 bg-gradient-to-b from-[#0c0306] to-[#040104] shadow-[0_40px_120px_rgba(0,0,0,0.65)] sm:max-h-[92vh] sm:rounded-2xl"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
          >
            {/* mobile grab handle */}
            <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-white/15 sm:hidden" />

            {/* gold hairline */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 hidden h-px bg-gradient-to-r from-transparent via-wbc-gold-light/70 to-transparent sm:block"
              aria-hidden
            />

            {/* close */}
            <button
              type="button"
              onClick={tryClose}
              aria-label="Close registration dialog"
              disabled={submitting}
              className="absolute right-3 top-3 z-[2] inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white/70 transition hover:border-white/30 hover:text-white disabled:opacity-40 sm:right-4 sm:top-4"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {!submitted ? (
              <>
                <div className="flex-shrink-0 px-5 pb-3 pt-7 sm:px-10 sm:pt-10">
                  <span className="font-condensed text-[10px] font-bold uppercase tracking-[0.4em] text-wbc-gold">
                    Senior Fighter · Registration
                  </span>
                  <h3 className="font-bebas mt-2 text-3xl leading-[0.95] tracking-wide text-white sm:mt-3 sm:text-5xl">
                    Step Into The Ring
                  </h3>
                  <p className="mt-2 max-w-xl font-barlow text-sm leading-relaxed text-white/65 sm:mt-3 sm:text-base">
                    Open category for amateur fighters{" "}
                    <span className="font-semibold text-white/90">aged {MIN_AGE} – {MAX_AGE}</span>. Your entry will be confirmed by email after our team reviews it.
                  </p>

                  {/* progress bar */}
                  <div className="mt-5 flex items-center gap-3">
                    <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-white/[0.07]">
                      <motion.div
                        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-wbc-red via-wbc-gold-light to-wbc-gold"
                        animate={{ width: `${progressPct}%` }}
                        transition={{ type: "spring", stiffness: 220, damping: 30 }}
                      />
                    </div>
                    <span className="font-condensed shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] text-white/55">
                      {filledCount}/{ALL_REQUIRED.length} filled
                    </span>
                  </div>
                </div>

                <form
                  ref={formRef}
                  action={formAction}
                  method="POST"
                  target="wbc_register_iframe"
                  onSubmit={handleSubmit}
                  className="flex-1 space-y-5 overflow-y-auto px-4 pb-32 pt-4 sm:space-y-6 sm:px-10 sm:pb-36"
                >
                  {/* 01 — ATHLETE PROFILE */}
                  <fieldset className={sectionCls}>
                    <SectionHeader
                      number="01"
                      title="Athlete Profile"
                      subtitle="Tell us who's stepping into the ring."
                      complete={sectionCompletion["01"]}
                    />
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <FieldLabel htmlFor="reg-name" required>
                          Full Name
                        </FieldLabel>
                        <input
                          ref={firstFieldRef}
                          id="reg-name"
                          required
                          type="text"
                          autoComplete="name"
                          name={FORM_ENTRY_IDS.name}
                          value={formData.name}
                          onChange={(e) => setField("name", e.target.value)}
                          placeholder="As written on your Aadhaar"
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <FieldLabel htmlFor="reg-dob" required>
                          Date of Birth
                        </FieldLabel>
                        <input
                          id="reg-dob"
                          required
                          type="date"
                          autoComplete="bday"
                          name={FORM_ENTRY_IDS.dob}
                          min={dobMin}
                          max={dobMax}
                          value={formData.dob}
                          onChange={(e) => setField("dob", e.target.value)}
                          className={inputCls}
                        />
                        {formData.dob && (
                          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-1 font-condensed text-[10px] font-bold uppercase tracking-[0.2em]">
                            <span
                              className={`inline-block h-1.5 w-1.5 rounded-full ${
                                ageValid ? "bg-wbc-gold-light" : "bg-wbc-red-bright"
                              }`}
                            />
                            <span
                              className={
                                ageValid ? "text-wbc-gold-light" : "text-wbc-red-bright"
                              }
                            >
                              {ageValid
                                ? `Age ${AGE_RANGE_LABEL} eligible`
                                : `Outside age ${AGE_RANGE_LABEL}`}
                            </span>
                          </div>
                        )}
                      </div>

                      <div>
                        <FieldLabel htmlFor="reg-age" hint="auto from DOB">
                          Age
                        </FieldLabel>
                        <div className="relative">
                          <input
                            id="reg-age"
                            type="text"
                            readOnly
                            tabIndex={-1}
                            aria-readonly="true"
                            name={FORM_ENTRY_IDS.age}
                            value={age != null ? String(age) : ""}
                            placeholder="—"
                            className={`${inputCls} cursor-default border-dashed bg-white/[0.015] pr-14 text-wbc-gold-light focus:ring-0`}
                          />
                          <span className="font-condensed pointer-events-none absolute inset-y-0 right-3 flex items-center text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
                            yrs
                          </span>
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <FieldLabel required>Gender</FieldLabel>
                        {/* Gender as toggle pills — clearer UX than a select */}
                        <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Gender">
                          {["Male", "Female"].map((g) => {
                            const active = formData.gender === g;
                            return (
                              <button
                                key={g}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                onClick={() => setField("gender", g)}
                                className={`font-condensed inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-3.5 text-sm font-bold uppercase tracking-[0.18em] transition-all duration-200 ${
                                  active
                                    ? "border-wbc-gold bg-wbc-gold/[0.14] text-wbc-gold-light shadow-[0_0_24px_rgba(212,160,23,0.18)]"
                                    : "border-white/10 bg-white/[0.03] text-white/65 hover:border-white/25 hover:text-white"
                                }`}
                              >
                                {active && <CheckIcon />}
                                {g}
                              </button>
                            );
                          })}
                        </div>
                        {/* Hidden field carries value into Google Form POST */}
                        <input
                          type="hidden"
                          name={FORM_ENTRY_IDS.gender}
                          value={formData.gender}
                        />
                      </div>
                    </div>
                  </fieldset>

                  {/* 02 — COMPETITION */}
                  <fieldset className={sectionCls}>
                    <SectionHeader
                      number="02"
                      title="Competition"
                      subtitle="Body data and the weight class you'll fight in."
                      complete={sectionCompletion["02"]}
                    />
                    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                      <div>
                        <FieldLabel htmlFor="reg-weight" required hint="kg">
                          Weight
                        </FieldLabel>
                        <input
                          id="reg-weight"
                          required
                          type="number"
                          step="0.1"
                          min="30"
                          max="200"
                          inputMode="decimal"
                          name={FORM_ENTRY_IDS.weight}
                          value={formData.weight}
                          onChange={(e) => setField("weight", e.target.value)}
                          placeholder="67.5"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <FieldLabel htmlFor="reg-height" required hint="cm">
                          Height
                        </FieldLabel>
                        <input
                          id="reg-height"
                          required
                          type="number"
                          min="120"
                          max="230"
                          inputMode="numeric"
                          name={FORM_ENTRY_IDS.height}
                          value={formData.height}
                          onChange={(e) => setField("height", e.target.value)}
                          placeholder="175"
                          className={inputCls}
                        />
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <FieldLabel
                          htmlFor="reg-weightCategory"
                          required
                          hint={formData.gender ? formData.gender : "pick gender first"}
                        >
                          Weight Category
                        </FieldLabel>
                        <select
                          id="reg-weightCategory"
                          required
                          name={FORM_ENTRY_IDS.weightCategory}
                          value={formData.weightCategory}
                          onChange={(e) => setField("weightCategory", e.target.value)}
                          disabled={!formData.gender}
                          className={`${inputCls} ${
                            !formData.gender ? "cursor-not-allowed opacity-60" : ""
                          }`}
                        >
                          <option value="">
                            {formData.gender ? "Select category" : "Pick gender first"}
                          </option>
                          {weightCats.map((w) => (
                            <option key={w} value={w}>
                              {w}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </fieldset>

                  {/* 03 — CONTACT & ADDRESS */}
                  <fieldset className={sectionCls}>
                    <SectionHeader
                      number="03"
                      title="Contact & Address"
                      subtitle="Where we'll send your confirmation email and updates."
                      complete={sectionCompletion["03"]}
                    />
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <FieldLabel htmlFor="reg-mobile" required hint="10 digits">
                          Mobile
                        </FieldLabel>
                        <input
                          id="reg-mobile"
                          required
                          type="tel"
                          autoComplete="tel"
                          inputMode="numeric"
                          pattern="[0-9]{10}"
                          maxLength={10}
                          name={FORM_ENTRY_IDS.mobile}
                          value={formData.mobile}
                          onChange={(e) => setField("mobile", e.target.value.replace(/\D/g, ""))}
                          placeholder="9876543210"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <FieldLabel htmlFor="reg-email" required>
                          Email
                        </FieldLabel>
                        <input
                          id="reg-email"
                          required
                          type="email"
                          autoComplete="email"
                          name={FORM_ENTRY_IDS.email}
                          value={formData.email}
                          onChange={(e) => setField("email", e.target.value)}
                          placeholder="you@example.com"
                          className={inputCls}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <FieldLabel htmlFor="reg-state" required>
                          State
                        </FieldLabel>
                        <select
                          id="reg-state"
                          required
                          name={FORM_ENTRY_IDS.state}
                          value={formData.state}
                          onChange={(e) => setField("state", e.target.value)}
                          className={inputCls}
                        >
                          <option value="">Select state</option>
                          {INDIAN_STATES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <FieldLabel htmlFor="reg-district" required>
                          District
                        </FieldLabel>
                        <input
                          id="reg-district"
                          required
                          type="text"
                          autoComplete="address-level2"
                          name={FORM_ENTRY_IDS.district}
                          value={formData.district}
                          onChange={(e) => setField("district", e.target.value)}
                          placeholder="e.g. New Delhi"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <FieldLabel htmlFor="reg-pincode" required hint="6 digits">
                          Pincode
                        </FieldLabel>
                        <input
                          id="reg-pincode"
                          required
                          type="text"
                          autoComplete="postal-code"
                          inputMode="numeric"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          name={FORM_ENTRY_IDS.pincode}
                          value={formData.pincode}
                          onChange={(e) => setField("pincode", e.target.value.replace(/\D/g, ""))}
                          placeholder="110001"
                          className={inputCls}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <FieldLabel htmlFor="reg-address" required>
                          Full Address
                        </FieldLabel>
                        <textarea
                          id="reg-address"
                          required
                          rows={2}
                          autoComplete="street-address"
                          name={FORM_ENTRY_IDS.address}
                          value={formData.address}
                          onChange={(e) => setField("address", e.target.value)}
                          placeholder="House / street / locality"
                          className={`${inputCls} resize-none`}
                        />
                      </div>
                    </div>
                  </fieldset>

                  {/* 04 — MEDICAL & KIT */}
                  <fieldset className={sectionCls}>
                    <SectionHeader
                      number="04"
                      title="Medical & Kit"
                      subtitle="Safety basics and your event uniform size."
                      complete={sectionCompletion["04"]}
                    />
                    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                      <div>
                        <FieldLabel htmlFor="reg-bloodGroup" required>
                          Blood Group
                        </FieldLabel>
                        <select
                          id="reg-bloodGroup"
                          required
                          name={FORM_ENTRY_IDS.bloodGroup}
                          value={formData.bloodGroup}
                          onChange={(e) => setField("bloodGroup", e.target.value)}
                          className={inputCls}
                        >
                          <option value="">Select</option>
                          {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((b) => (
                            <option key={b} value={b}>
                              {b}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <FieldLabel htmlFor="reg-uniformSize" required>
                          Uniform Size
                        </FieldLabel>
                        <select
                          id="reg-uniformSize"
                          required
                          name={FORM_ENTRY_IDS.uniformSize}
                          value={formData.uniformSize}
                          onChange={(e) => setField("uniformSize", e.target.value)}
                          className={inputCls}
                        >
                          <option value="">Select size</option>
                          {["XS", "S", "M", "L", "XL", "XXL", "XXXL"].map((sz) => (
                            <option key={sz} value={sz}>
                              {sz}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <FieldLabel htmlFor="reg-guardian" required hint="someone we can call">
                          Emergency Contact
                        </FieldLabel>
                        <input
                          id="reg-guardian"
                          required
                          type="tel"
                          autoComplete="tel"
                          inputMode="numeric"
                          pattern="[0-9]{10}"
                          maxLength={10}
                          name={FORM_ENTRY_IDS.guardianMobile}
                          value={formData.guardianMobile}
                          onChange={(e) => setField("guardianMobile", e.target.value.replace(/\D/g, ""))}
                          placeholder="9876543210"
                          className={inputCls}
                        />
                      </div>
                    </div>
                  </fieldset>

                  {errorMsg && (
                    <div
                      role="alert"
                      className="flex items-start gap-3 rounded-lg border border-wbc-red/50 bg-wbc-red/10 px-4 py-3 text-sm text-wbc-red-bright"
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                        <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M9 5V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <circle cx="9" cy="13" r="0.9" fill="currentColor" />
                      </svg>
                      <span>{errorMsg}</span>
                    </div>
                  )}
                </form>

                {/* Sticky bottom action bar — submit always reachable */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] border-t border-white/[0.07] bg-gradient-to-t from-[#040104] via-[#070205]/95 to-[#070205]/85 px-4 py-3 backdrop-blur-md sm:px-10 sm:py-4">
                  <div className="pointer-events-auto flex items-center justify-between gap-3">
                    <p className="hidden font-barlow text-xs leading-relaxed text-white/45 sm:block sm:text-sm">
                      <span className="text-wbc-red-bright">*</span> required ·{" "}
                      we&apos;ll confirm by email after review.
                    </p>
                    <p className="font-condensed text-[10px] font-bold uppercase tracking-[0.2em] text-white/55 sm:hidden">
                      {progressPct}% filled
                    </p>
                    <button
                      ref={submitRef}
                      type="submit"
                      form={undefined}
                      onClick={() => formRef.current?.requestSubmit()}
                      disabled={submitting}
                      className="font-condensed inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-wbc-red px-6 py-3.5 text-xs font-bold uppercase tracking-[0.22em] text-white shadow-[0_18px_40px_rgba(214,0,28,0.35)] transition hover:bg-wbc-red-bright disabled:cursor-not-allowed disabled:opacity-70 sm:flex-initial sm:px-8 sm:text-sm"
                    >
                      {submitting ? (
                        <>
                          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                          Submitting…
                        </>
                      ) : (
                        <>
                          Submit Registration
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                            <path
                              d="M2 7H12M12 7L8 3M12 7L8 11"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center sm:px-10">
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 360, damping: 20 }}
                  className="flex h-16 w-16 items-center justify-center rounded-full border border-wbc-gold/40 bg-wbc-gold/10 text-wbc-gold-light"
                >
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
                    <path
                      d="M5 11.5L9.5 16L17 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>
                <span className="font-condensed mt-6 text-[10px] font-bold uppercase tracking-[0.4em] text-wbc-gold">
                  Entry Received
                </span>
                <h3 className="font-bebas mt-3 text-3xl leading-[0.95] tracking-wide text-white sm:text-5xl">
                  See You In The Ring
                </h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-white/55 sm:text-base">
                  Your registration has been recorded. A confirmation email with the{" "}
                  <span className="text-wbc-gold-light">20 July intake</span> checklist will arrive shortly — keep an eye on your inbox.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={onClose}
                    className="font-condensed inline-flex items-center justify-center rounded-md bg-wbc-gold-light px-8 py-3.5 text-xs font-bold uppercase tracking-[0.22em] text-[#1a0509] transition hover:bg-wbc-gold sm:text-sm"
                  >
                    Done
                  </button>
                  <button
                    type="button"
                    onClick={handleRegisterAnother}
                    className="font-condensed inline-flex items-center justify-center rounded-md border border-white/30 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.22em] text-white/85 transition hover:border-white hover:text-white sm:text-sm"
                  >
                    Register Another
                  </button>
                </div>
              </div>
            )}

            {/* Footer help line — hide while filling to keep focus on the form/sticky CTA */}
            {submitted && (
              <div className="border-t border-white/[0.06] bg-black/30 px-6 py-4 text-center sm:px-10">
                <p className="text-xs leading-relaxed text-white/45 sm:text-sm">
                  Trouble submitting?{" "}
                  <a
                    href="mailto:admin@wbcmuaythaiindia.com"
                    className="text-wbc-gold-light underline-offset-4 hover:underline"
                  >
                    admin@wbcmuaythaiindia.com
                  </a>
                </p>
              </div>
            )}
          </motion.div>

          {/* Hidden iframe target — Google Forms POST lands here, no page nav */}
          <iframe
            name="wbc_register_iframe"
            title="WBC registration sink"
            onLoad={handleIframeLoad}
            style={{ display: "none" }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
